import { useState, useRef, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import MermaidBlock from "./MermaidBlock";
import ReportCitationButton, {
  sanitizeCiteTitle,
} from "./ReportCitationButton";
import CitationFooter from "./CitationFooter";
import {
  escapeCitations,
  healMarkdown,
  stripCitationFooter,
  ANNOTATION_RE,
} from "./utils/reportUtils";

/* ═══════════════════════════════════════════════════════════════════════════
   Citation injection pipeline
   ═══════════════════════════════════════════════════════════════════════════ */

const CITE_RE = /@@CITE:(\d+)@@/g;

const citationTokensToMarkdown = (value) =>
  (value || "").replace(/@@CITE:(\d+)@@/g, "[$1]");

const getSectionHeading = (markdown, start) => {
  const before = markdown.slice(0, Math.max(0, start));
  const headings = before.match(/^#{1,6}\s+.+$/gm);
  return headings?.[headings.length - 1]?.trim() || "";
};

const findRawRange = (preparedSlice, sourceText) => {
  const rawSlice = citationTokensToMarkdown(preparedSlice).trim();
  if (!rawSlice || !sourceText) return null;
  const first = sourceText.indexOf(rawSlice);
  const last = sourceText.lastIndexOf(rawSlice);
  if (first < 0 || first !== last) return null;
  return { start: first, end: first + rawSlice.length, text: rawSlice };
};

const makeNodeRawRange = (node, liveRef) => {
  const preparedText = liveRef.current.preparedText || "";
  const sourceText = liveRef.current.sourceText || "";
  const startOffset = node?.position?.start?.offset;
  const endOffset = node?.position?.end?.offset;
  if (!Number.isFinite(startOffset) || !Number.isFinite(endOffset)) return null;
  return findRawRange(preparedText.slice(startOffset, endOffset), sourceText);
};

const makeBlockPayload = (node, kind, liveRef, initialRange) => {
  const sourceText = liveRef.current.sourceText || "";
  const initial = initialRange || makeNodeRawRange(node, liveRef);
  if (!initial) return null;

  let { start, end, text } = initial;
  let blockType = kind;

  if (kind === "heading") {
    const level = text.match(/^(#{1,6})\s/)?.[1]?.length || 1;
    if (level > 1) {
      const rest = sourceText.slice(end);
      const next = new RegExp(`\\n#{1,${level}}\\s+`).exec(rest);
      const sectionEnd = next ? end + next.index : sourceText.length;
      text = sourceText.slice(start, sectionEnd).trimEnd();
      end = start + text.length;
      blockType = "section";
    } else {
      blockType = "title";
    }
  }

  return {
    text,
    display_text: text,
    mapped: true,
    block_type: blockType,
    start,
    end,
    section_heading:
      blockType === "section" ? text.split("\n", 1)[0].trim() : getSectionHeading(sourceText, start),
    prefix: sourceText.slice(Math.max(0, start - 500), start),
    suffix: sourceText.slice(end, end + 500),
  };
};

const BlockHandle = ({ node, kind, liveRef, children }) => {
  const label = kind === "heading" ? "Edit section" : "Edit paragraph";
  const rawRange = makeNodeRawRange(node, liveRef);
  const selectBlock = () => {
    const payload = makeBlockPayload(node, kind, liveRef, rawRange);
    if (payload) liveRef.current.onBlockSelection?.(payload);
  };

  return (
    <div
      className="group/report-block relative"
      data-report-block="true"
      data-report-block-kind={kind}
      data-report-raw-start={Number.isFinite(rawRange?.start) ? rawRange.start : undefined}
      data-report-raw-end={Number.isFinite(rawRange?.end) ? rawRange.end : undefined}
    >
      <button
        type="button"
        data-report-block-handle="true"
        data-export-exclude="true"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          selectBlock();
        }}
        className="absolute -left-8 top-1 rounded-md border border-transparent px-1.5 py-0.5 text-[12px] text-mutedText/70 opacity-0 transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText group-hover/report-block:opacity-100 focus:opacity-100"
        title={label}
        aria-label={label}
      >
        ⋮
      </button>
      {children}
    </div>
  );
};

const injectCitations = (
  children,
  citationMap,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  onSourceClick,
  activeSourceId,
  citationInteractions,
) => {
  if (!citationMap || Object.keys(citationMap).length === 0) return children;
  const process = (child) => {
    if (typeof child !== "string") return child;
    if (!CITE_RE.test(child)) return child;
    CITE_RE.lastIndex = 0;
    const parts = child.split(CITE_RE);
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        const idx = parseInt(part, 10);
        const citation = citationMap[idx];
        if (citation)
          return (
            <ReportCitationButton
              key={`rc${i}`}
              index={idx}
              citation={citation}
              hoveredRef={hoveredRef}
              onHoverIn={onHoverIn}
              onHoverOut={onHoverOut}
              onSourceClick={onSourceClick}
              activeSourceId={activeSourceId}
              citationInteractions={citationInteractions}
            />
          );
        return `[${part}]`;
      }
      return part;
    });
  };
  if (Array.isArray(children)) return children.flatMap(process);
  return process(children);
};

/* ═══════════════════════════════════════════════════════════════════════════
   buildMdComponents — custom ReactMarkdown component map
   ═══════════════════════════════════════════════════════════════════════════ */

export const buildMdComponents = (
  hoveredRef,
  onHoverIn,
  onHoverOut,
  liveRef,
) => {
  const pc = (children) =>
    injectCitations(
      children,
      liveRef.current.citationMap,
      hoveredRef,
      onHoverIn,
      onHoverOut,
      liveRef.current.onSourceClick,
      liveRef.current.activeSourceId,
      liveRef.current.citationInteractions,
    );

  return {
    p: ({ node, children }) => (
      <BlockHandle node={node} kind="paragraph" liveRef={liveRef}>
        <p className="mb-3 last:mb-0 leading-[1.8]">{pc(children)}</p>
      </BlockHandle>
    ),
    h1: ({ node, children }) => (
      <BlockHandle node={node} kind="heading" liveRef={liveRef}>
        <h1 className="mb-3 mt-8 text-xl font-semibold text-mainText first:mt-0">
          {pc(children)}
        </h1>
      </BlockHandle>
    ),
    h2: ({ node, children }) => (
      <BlockHandle node={node} kind="heading" liveRef={liveRef}>
        <h2 className="mb-2.5 mt-7 border-b border-dropdownDivider pb-1.5 text-lg font-semibold text-mainText first:mt-0">
          {pc(children)}
        </h2>
      </BlockHandle>
    ),
    h3: ({ node, children }) => (
      <BlockHandle node={node} kind="heading" liveRef={liveRef}>
        <h3 className="mb-2 mt-5 text-base font-semibold text-mainText first:mt-0">
          {pc(children)}
        </h3>
      </BlockHandle>
    ),
    h4: ({ node, children }) => (
      <BlockHandle node={node} kind="heading" liveRef={liveRef}>
        <h4 className="mb-1.5 mt-3 text-sm font-semibold text-mainText first:mt-0">
          {pc(children)}
        </h4>
      </BlockHandle>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-mainText">
        {pc(children)}
      </strong>
    ),
    em: ({ children }) => <em>{pc(children)}</em>,
    ul: ({ children }) => (
      <ul className="mb-3 ml-5 list-disc space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-3 ml-5 list-decimal space-y-1">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-[1.8]">{pc(children)}</li>,
    a: ({ href, children }) => {
      const title = typeof children === "string" ? children : href;
      const citation = { url: href, title };
      const citationKey = href;
      const isActive =
        liveRef.current.activeSourceId &&
        liveRef.current.activeSourceId === citationKey;
      const interactions = liveRef.current.citationInteractions?.[citationKey];
      const label = sanitizeCiteTitle(title, href, 0);
      return (
        <ReportCitationButton
          index={0}
          citation={citation}
          hoveredRef={hoveredRef}
          onHoverIn={() => {}}
          onHoverOut={() => {}}
          onSourceClick={liveRef.current.onSourceClick}
          activeSourceId={liveRef.current.activeSourceId}
          citationInteractions={liveRef.current.citationInteractions}
        />
      );
    },
    code: ({ inline, className, children }) => {
      if (inline)
        return (
          <code className="rounded border border-surfaceBorder bg-mainBg/60 px-1 py-0.5 font-mono text-[13px] text-mainText">
            {children}
          </code>
        );
      const lang = (className || "").replace("language-", "");

      /* ── Mermaid diagram rendering ── */
      if (lang === "mermaid") {
        return <MermaidBlock>{children}</MermaidBlock>;
      }

      return (
        <div className="my-3 overflow-hidden rounded-lg border border-surfaceBorder bg-mainBg/60">
          <div className="flex items-center justify-between border-b border-dropdownDivider bg-surface/60 px-4 py-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-mutedText">
              {lang || "code"}
            </span>
          </div>
          <pre className="overflow-x-auto p-4">
            <code className="font-mono text-[13px] leading-relaxed text-mainText">
              {children}
            </code>
          </pre>
        </div>
      );
    },
    pre: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => (
      <blockquote className="my-3 rounded-r-lg border-l-2 border-velBlue/25 bg-mainBg/35 py-2 pl-4 pr-3 text-mutedText italic">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-6 border-dropdownDivider" />,
    table: ({ children }) => (
      <div className="my-5 overflow-x-auto rounded-lg border border-surfaceBorder">
        <table className="min-w-full border-collapse text-[13px]">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-mainBg/60">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="border-b border-surfaceBorder px-4 py-2.5 text-left text-[12px] font-semibold uppercase tracking-wider text-mutedText">
        {pc(children)}
      </th>
    ),
    td: ({ children }) => (
      <td className="border-b border-dropdownDivider px-4 py-2.5 text-mainText">
        {pc(children)}
      </td>
    ),
    tr: ({ children, ...props }) => (
      <tr className="even:bg-mainBg/30" {...props}>
        {children}
      </tr>
    ),
    mark: ({ children }) => (
      <mark className="rounded-sm bg-yellow-500/20 px-0.5 text-inherit">
        {children}
      </mark>
    ),
  };
};

/* ═══════════════════════════════════════════════════════════════════════════
   ReportMarkdown — main export
   ═══════════════════════════════════════════════════════════════════════════ */

const ReportMarkdown = ({
  text,
  citations,
  streaming,
  onSourceClick,
  activeSourceId,
  citationInteractions,
  sourceText,
  onBlockSelection,
}) => {
  const [, setHoveredCite] = useState(null);
  const hoveredRef = useRef(null);

  const baseCitationMap = useMemo(() => {
    const map = {};
    if (citations && citations.length > 0) {
      citations.forEach((c) => {
        map[c.index] = c;
      });
    }
    return map;
  }, [citations]);

  const citationMap = useMemo(() => {
    if (!streaming || !text) return baseCitationMap;
    const refRe = /\[(\d+)\]/g;
    let m;
    let hasNew = false;
    const extras = {};
    while ((m = refRe.exec(text)) !== null) {
      const idx = parseInt(m[1], 10);
      if (!baseCitationMap[idx] && !extras[idx]) {
        extras[idx] = { index: idx, title: `Source ${idx}`, url: "" };
        hasNew = true;
      }
    }
    if (!hasNew) return baseCitationMap;
    return { ...baseCitationMap, ...extras };
  }, [baseCitationMap, streaming, text]);

  const onHoverIn = useCallback((idx) => {
    hoveredRef.current = idx;
    setHoveredCite(idx);
  }, []);
  const onHoverOut = useCallback(() => {
    hoveredRef.current = null;
    setHoveredCite(null);
  }, []);

  const prepared = useMemo(() => {
    if (!text) return "";
    let s = stripCitationFooter(text);
    s = s.replace(ANNOTATION_RE, "");
    s = citationTokensToMarkdown(s);
    s = escapeCitations(s);
    if (streaming) s = healMarkdown(s);
    return s;
  }, [text, citations, streaming]);

  const liveRef = useRef({});
  liveRef.current = {
    citationMap,
    onSourceClick,
    activeSourceId,
    citationInteractions,
    sourceText: sourceText || text,
    preparedText: prepared,
    onBlockSelection,
  };

  const components = useMemo(
    () => buildMdComponents(hoveredRef, onHoverIn, onHoverOut, liveRef),
    [onHoverIn, onHoverOut],
  );

  if (!text) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={components}
    >
      {prepared}
    </ReactMarkdown>
  );
};

export default ReportMarkdown;
